"use client";
/* Hero — editorial split with massive typography */
import React from "react";
import { Eyebrow } from "./Eyebrow";
import { Button } from "./Button";
import { ScrollReveal } from "./ScrollReveal";

interface HeroProps {
  className?: string;
  ctaLabel?: string;
  ctaHref?: string;
}

export const Hero = ({
  className,
  ctaLabel = "View Research",
  ctaHref = "/research",
}: HeroProps) => {
  return (
    <section
      className={`relative min-h-[85vh] flex items-center overflow-hidden ${className || ""}`}
    >
      {/* Background gradient orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/4 -right-1/4 h-[600px] w-[600px] rounded-full bg-[var(--color-accent)]/5 blur-[120px]" />
        <div className="absolute -bottom-1/4 -left-1/4 h-[500px] w-[500px] rounded-full bg-[var(--color-accent)]/3 blur-[100px]" />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-6 sm:px-8 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
          {/* Left: Typography */}
          <div>
            <ScrollReveal>
              <Eyebrow className="mb-8">Research Lab</Eyebrow>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <h1 className="mb-8 font-serif text-[var(--type-scale-7)] font-bold leading-[0.95] tracking-tight text-[var(--color-ink)]">
                Advancing
                <br />
                the Frontiers
                <br />
                of{" "}
                <span className="text-[var(--color-accent)]">
                  Generative AI
                </span>
              </h1>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <p className="mb-10 max-w-lg text-lg leading-relaxed text-[var(--color-muted)]">
                Research, innovation, and collaboration at IIT Jodhpur with Meta and IndiaAI mission, pushing
                the boundaries of what AI can achieve through rigorous inquiry
                and open science.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <div className="flex flex-wrap gap-4">
                <Button variant="primary" size="lg" href={ctaHref}>
                  {ctaLabel}
                </Button>
                <Button variant="secondary" size="lg" href="/publications">
                  Publications
                </Button>
              </div>
            </ScrollReveal>
          </div>

          {/* Right: Editorial image/abstract */}
          <ScrollReveal delay={200}>
            <div className="relative hidden lg:block">
              <div className="rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-surface-elevated)] p-2 shadow-[0_32px_64px_rgba(0,0,0,0.06)]">
                <div className="relative aspect-[4/5] overflow-hidden rounded-[calc(var(--radius-lg)-0.25rem)]">
                  <img
                    src="/api/media/file/Departments.jpg"
                    alt="Srijan research department"
                    className="h-full w-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:scale-[1.02]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-ink)]/20 via-transparent to-transparent" />
                </div>
              </div>
              {/* Floating accent card */}
              <div className="absolute -bottom-6 -left-6 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-6 py-4 shadow-[0_16px_48px_rgba(0,0,0,0.08)]">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[var(--color-accent)]">
                  Srijan:CoE
                </p>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Est. 2024
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};
